import { useEffect, useMemo, useState } from 'react'
import { Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useReconciliation, useSaveReconciliationVotes } from '../../hooks/useLobbyReconciliation'
import type { LegislatorVote, ReconciliationDto, SentimentDisposition, VoteUpsert } from '../../types/lobbyDay'
import { AskReconciliationSection } from './reconciliation/AskReconciliationSection'
import { ReconciliationScorecard } from './reconciliation/ReconciliationScorecard'
import { ApplyBar } from './reconciliation/ApplyBar'

type EditKey = string // `${askId}:${legislatorId}`
// notes always carries the row's full current value (whether or not notes itself was the field
// just touched) -- see LegislatorVoteRow's onChange contract -- so a vote-only edit never wipes
// an existing note when the batch is sent (Task 13).
interface Edit { vote: LegislatorVote; override: SentimentDisposition | null; notes: string | null }

const keyOf = (askId: string, legId: string): EditKey => `${askId}:${legId}`

// Merge server data with local edits so the grid reflects unsaved vote/notes changes immediately.
function applyEdits(data: ReconciliationDto, edits: Record<EditKey, Edit>): ReconciliationDto {
  return {
    ...data,
    asks: data.asks.map((ask) => ({
      ...ask,
      legislators: ask.legislators.map((row) => {
        const e = edits[keyOf(ask.askId, row.legislatorId)]
        return e ? { ...row, vote: e.vote, dispositionOverride: e.override, notes: e.notes } : row
      }),
    })),
  }
}

export function ReconciliationTab({ eventId }: { eventId: string }) {
  const { data, isLoading, error } = useReconciliation(eventId)
  const saveVotes = useSaveReconciliationVotes(eventId)
  const [edits, setEdits] = useState<Record<EditKey, Edit>>({})
  // Wave 3 (C.3): the ApplyBar's count must come from the SAVE response, not (only) the
  // reconciliation query's summary -- the query is invalidated on save and eventually agrees, but
  // reading straight off the mutation result means the strip reflects what just happened without
  // waiting on a refetch round-trip, and makes a regression that dropped the auto-apply call
  // (reverting to the old separate-apply flow) show up here rather than being masked by a query
  // value that happens to still be right from a previous load.
  const [lastAppliedCount, setLastAppliedCount] = useState<number | null>(null)

  // Fix wave: a `lastAppliedCount` that is never cleared goes sticky forever -- once this
  // component's own save sets it, the strip would keep showing that stale local number even
  // after the query later refetches a DIFFERENT true count (e.g. another organizer applying via
  // the retained POST .../apply endpoint). Clear it whenever the query's underlying data changes
  // for ANY reason, including this component's own post-save refetch -- by the time that refetch
  // resolves, `merged.summary.attendeesTagged` already reflects the same (or a superseding) fresh
  // number, so falling back to it here causes no visible flicker.
  useEffect(() => {
    setLastAppliedCount(null)
  }, [data])

  const dirty = Object.keys(edits).length > 0
  const merged = useMemo(() => (data ? applyEdits(data, edits) : null), [data, edits])
  const attendeesTagged = lastAppliedCount ?? merged?.summary.attendeesTagged ?? 0

  const onRowChange = (askId: string, legId: string, vote: LegislatorVote, override: SentimentDisposition | null, notes: string | null) =>
    setEdits((prev) => ({ ...prev, [keyOf(askId, legId)]: { vote, override, notes } }))

  // Wave 3 (C.3): saving votes applies sentiment tags in the same request -- no separate Apply
  // step. Both the toast and the ApplyBar strip read their count from this one response.
  //
  // Fix wave: never fail silently. SaveVotesAsync/ApplyReconciliationAsync commit independently
  // (no shared transaction), so the backend distinguishes two failure shapes and this must match
  // each honestly:
  //   - mutateAsync REJECTS (total failure -- e.g. a scope violation, or the vote save itself
  //     failed): nothing persisted. Edits stay set (dirty stays true) and an error toast fires --
  //     clearing edits here would hide unsaved work behind a UI that looks like it saved fine.
  //   - mutateAsync RESOLVES with taggingFailed=true (partial success): the votes themselves DID
  //     persist, so edits are cleared (dirty goes false) same as a normal save, but the toast
  //     says tagging failed rather than claiming the (meaningless, 0) attendeesTagged count --
  //     leaving them set here would nag about edits that already saved. lastAppliedCount is left
  //     untouched (not overwritten with 0) since the tagged count genuinely did not change.
  const onSave = async () => {
    const votes: VoteUpsert[] = Object.entries(edits).map(([k, e]) => {
      const [askId, legislatorId] = k.split(':')
      return { askId, legislatorId, vote: e.vote, dispositionOverride: e.override, notes: e.notes }
    })
    try {
      const res = await saveVotes.mutateAsync({ votes })
      setEdits({})
      if (res.taggingFailed) {
        toaster.create({
          type: 'warning',
          title: 'Votes saved',
          description: 'Automatic tagging failed. Saving again will retry it.',
        })
      } else {
        setLastAppliedCount(res.attendeesTagged)
        toaster.create({ type: 'success', title: 'Votes saved', description: `${res.attendeesTagged} attendees tagged` })
      }
    } catch {
      toaster.create({
        type: 'error',
        title: 'Failed to save votes',
        description: 'Your changes were not saved. Please try again.',
      })
    }
  }

  if (isLoading) return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>
  if (!!error || !merged) return <Text color="red.500">Failed to load reconciliation.</Text>

  if (merged.asks.length === 0) {
    return <Text py={6} color="gray.500">Add asks and schedule meetings first — then record how each legislator voted.</Text>
  }

  return (
    <VStack align="stretch" gap={4}>
      <ReconciliationScorecard summary={merged.summary} />
      {dirty && (
        <HStack justify="flex-end">
          <Button size="sm" colorPalette="brand" onClick={onSave} loading={saveVotes.isPending}>Save votes</Button>
        </HStack>
      )}
      {merged.asks.map((ask) => (
        <AskReconciliationSection key={ask.askId} eventId={eventId} ask={ask} onRowChange={onRowChange} />
      ))}
      <ApplyBar dirty={dirty} attendeesTagged={attendeesTagged} />
    </VStack>
  );
}
